import { Clock, User } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { NAVIGATION } from "@/app/navigation";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useAuth } from "@/lib/auth";
import { fullName, useEmployees } from "@/lib/data";

/** ⌘K: Seiten und Mitarbeiter (nach steward/Search.tsx). */
export function CommandMenu({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const employees = useEmployees();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title={t("nav.search")} description={t("nav.search_placeholder")}>
      <CommandInput placeholder={t("nav.search_placeholder")} />
      <CommandList>
        <CommandEmpty>{t("nav.no_results")}</CommandEmpty>
        <CommandGroup heading={t("nav.pages")}>
          {NAVIGATION.flatMap((s) => s.items)
            .filter((i) => hasRole(...i.roles))
            .map((i) => (
              <CommandItem key={i.to} onSelect={() => go(i.to)}>
                <i.icon /> {t(i.labelKey)}
              </CommandItem>
            ))}
        </CommandGroup>
        {(employees.data?.length ?? 0) > 0 && (
          <CommandGroup heading={t("common.employees")}>
            {employees.data!.map((e) => (
              <CommandItem key={e.id} value={`${fullName(e)} ${e.personnel_number ?? ""}`} onSelect={() => go(`/app/times?employee=${e.id}`)}>
                <User /> {fullName(e)}
                <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="size-3!" /> {e.personnel_number}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
